import type { Ref } from 'react'
import React from 'react'
import classNames from 'classnames'

import { Avatar } from '../Avatar/Avatar'
import { Button } from '../Button/Button'

import './styles.css'

export type OrganizationBadgeProps = {
  name?: string | null
  photoUrl?: string | null
  isButton?: boolean
  onClick?: () => void
  isActive?: boolean
  /** Render the organization name next to the avatar. Default: true. Set
   *  to `false` for compact contexts (e.g. desktop Electron) where only
   *  the avatar should show. The `name` prop is still used for the
   *  Avatar's initials fallback. */
  showName?: boolean
  className?: string
  'data-test-id'?: string
  ref?: Ref<HTMLButtonElement>
}

export function OrganizationBadge({
  name,
  photoUrl,
  isButton = true,
  onClick,
  isActive,
  showName = true,
  className,
  'data-test-id': testId,
  ref,
}: OrganizationBadgeProps) {
  const content = (
    <div className="flexbox flexbox-align-center flexbox-column-gap-8">
      <Avatar
        size="xsmall"
        className="flexbox-no-shrink"
        name={name ?? undefined}
        photoUrl={photoUrl ?? undefined}
      />
      {showName && name && (
        <span
          style={{ maxWidth: 180 }}
          className="ellipsis shade-05 font-weight-bold font-size-large"
        >
          {name}
        </span>
      )}
    </div>
  )

  if (!isButton) {
    return <div className={className}>{content}</div>
  }

  // No name → square 32×32 button to match an IconButton at `size="normal"`.
  const isAvatarOnly = !showName || !name

  return (
    <Button
      ref={ref}
      size="normal"
      variant="tertiary"
      isActive={isActive}
      onClick={onClick}
      data-test-id={testId}
      className={classNames(
        'organization-badge-button',
        isAvatarOnly
          ? 'organization-badge-button--avatar-only'
          : 'width-fit-content flexbox-justify-start ellipsis',
        className
      )}
    >
      {content}
    </Button>
  )
}
